import React, { useState } from 'react'
import {
  ChecklistItemStatus,
  FUND_RELEASE_RESEND_WAIT_TIME_SECONDS,
  FUND_RELEASE_SUPPORT_LINK,
  FundReleaseType,
  StatusData,
  calculateFutureDate,
  dateAndTime,
  dollars,
  errorToString,
  mdyyyy,
  getBadgeClass,
  getChecklistItemDate,
  getCurrentFundRelease,
  getDisbursedDateFromLabel,
  LoanFundingPhaseShared
} from '@oneethos/shared'
import { CheckboxIcon } from './checkbox-icon'
import { GoQuestion } from 'react-icons/go'
import { toast } from 'react-toastify'
import { Spinner } from '@fluentui/react'
import { Modal } from './modal'
import api from '../api-client'

const ChecklistItemFundRequest = ({ item, onClick, project, isExpanded, onProjectChange }) => {
  const status = LoanFundingPhaseShared.getLoanFundingChecklistItemStatus(item, project)
  const isRequestLabel = /Requested$/i.test(item?.label || '')
  const isApprovedLabel = /Approved$/i.test(item?.label || '')
  const isDisbursedLabel = /Disbursed$/i.test(item?.label || '')
  const isGenericAirtableLabel = !isRequestLabel && !isApprovedLabel && !isDisbursedLabel

  const hasChangeOrderPendingStatus = project.installersStatus === 'Change Order Pending'
  const shouldHideStatusBadge = hasChangeOrderPendingStatus && status !== ChecklistItemStatus.Completed

  const [sendingRequest, setSendingRequest] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const handleOpenModal = () => setShowModal(true)
  const handleCloseModal = () => setShowModal(false)

  const fundReleaseTypeForStep = item?.fundReleaseTypes?.[0]
  const shouldShowFundRelease = !!fundReleaseTypeForStep
  const fundReleaseAmount = fundReleaseTypeForStep &&
    project.fundReleaseAmounts?.[fundReleaseTypeForStep]

  // This handles the scenario where loanAmount is updated, requiring us to fetch the 
  // updated fund release amount from Airtable using the finalFundReleaseAmount value.
  const adjustedFundReleaseAmount = fundReleaseTypeForStep === FundReleaseType.FinalFundingRelease
    ? (project.finalFundReleaseAmount || fundReleaseAmount)
    : fundReleaseAmount

  const currentFundRelease = getCurrentFundRelease(project, fundReleaseTypeForStep)

  const sendFundReleaseRequest = () => {
    setSendingRequest(true)
    const payload = {
      fundReleaseType: fundReleaseTypeForStep,
      amount: adjustedFundReleaseAmount,
      requestedAt: new Date().toISOString(),
      agreementSigned: {
        browser: {},
        timestamp: null,
        agrees: false,
      }
    }

    api.post(`/loanapps/${project.id}/fund-release-request`, payload)
      .then((proj) => {
        const p = new StatusData(proj)
        onProjectChange(p)
        toast.success('Fund release request sent')
      }).catch(err => {
        toast.error(errorToString(err))
      }).finally(() => setSendingRequest(false))
  }

  const handleResendRequest = () => {
    setSendingRequest(true)
    api.post(`/loanapps/${project.id}/resend-fund-release-request`, {
      fundReleaseType: fundReleaseTypeForStep,
      fundReleaseAmount: adjustedFundReleaseAmount,
    })
      .then((proj) => {
        const p = new StatusData(proj)
        onProjectChange(p)
        toast.success('Fund release request resent')
      }).catch(err => {
        toast.error(errorToString(err))
      }).finally(() => setSendingRequest(false))
  }

  const sinceRequested = currentFundRelease?.requestedAt && !isNaN(currentFundRelease.requestedAt.getTime())
    ? new Date().getTime() - currentFundRelease.requestedAt.getTime()
    : 0
  const showResend = sinceRequested / 1000 > FUND_RELEASE_RESEND_WAIT_TIME_SECONDS
  const fundReleaseRequestExpiresDate = currentFundRelease?.requestedAt
    && !isNaN(currentFundRelease.requestedAt.getTime())
    ? calculateFutureDate(currentFundRelease.requestedAt, 7)
    : null
  const checklistItemDate = getChecklistItemDate(item, project)
  const hasFundReleaseExpired = LoanFundingPhaseShared.isFundReleaseExpired(currentFundRelease?.requestedAt)
  const bankerComments = project[item.dataField]

  if (!shouldShowFundRelease) {
    return null
  }

  return (
    <>
      <tr onClick={onClick} className='align-middle'>
        <td style={{ width: '30px' }}>
          <CheckboxIcon isCompleted={status === ChecklistItemStatus.Completed} checklistOwner={item.owner} />
        </td>
        {isRequestLabel && (
          <>
            <td style={{ maxWidth: '300px' }}>
              <div className="fw-bold mb-1 pointer">{item.label}</div>
              <div className={`text-muted small pointer ${isExpanded ? '' : 'd-block text-truncate'}`}>
                <span>Send release request to customer for: <b>{`${dollars(adjustedFundReleaseAmount)}`} </b>
                  <GoQuestion
                    className="position-relative pointer"
                    style={{ color: "var(--oe-blue)" }}
                    onClick={handleOpenModal} />
                </span>
                {!currentFundRelease?.agreementSigned?.agrees && status === ChecklistItemStatus.Completed &&
                  <div className={`${isExpanded ? '' : 'd-block text-truncate'}`}>
                    <div>Expires:
                      <b>
                        {fundReleaseRequestExpiresDate && dateAndTime(fundReleaseRequestExpiresDate)} (7 days)
                      </b>
                    </div>
                    <div>
                      {sendingRequest ? <Spinner /> : (
                        <>
                          <span>If not approved before expiry, you’ll need to resend.</span>
                          {showResend && !hasChangeOrderPendingStatus &&
                            <div className={`mt-2 ${isExpanded ? '' : 'd-block text-truncate'}`}>
                              <b>Issues? </b>
                              <button className="btn btn-primary btn-tiny" onClick={handleResendRequest}>
                                Resend Request
                              </button>
                            </div>
                          }
                        </>
                      )}
                    </div>
                  </div>}
              </div>
              {bankerComments &&
                <div className={`small text-danger ${isExpanded ? '' : 'd-block text-truncate'}`}>
                  <b>Funding Team Comments:</b> {bankerComments}
                </div>
              }
            </td>
            <td className='text-center'>
              {status === ChecklistItemStatus.Completed &&
                <span>{mdyyyy(checklistItemDate)}</span>}
              {!hasChangeOrderPendingStatus && !currentFundRelease && 
                <button
                  type="button"
                  className="btn btn-tiny btn-primary"
                  onClick={sendFundReleaseRequest}
                >
                  {sendingRequest ? <Spinner /> : "Send Request"}
                </button>}
              {(!hasChangeOrderPendingStatus && hasFundReleaseExpired && status !== ChecklistItemStatus.Completed) &&
                <button
                  type="button"
                  className="btn btn-tiny btn-primary"
                  onClick={handleResendRequest}
                >
                  {sendingRequest ? <Spinner /> : "Send Request"}
                </button>
              }
            </td>
          </>
        )}
        {isApprovedLabel &&
          <>
            <td style={{ maxWidth: '120px' }}>
              <div className="fw-bold mb-1 pointer">{item.label}</div>
              <div className={`text-muted small pointer ${isExpanded ? '' : 'd-block text-truncate'}`}>
                <span>
                  {item.description}
                  {fundReleaseRequestExpiresDate && ` at ${dateAndTime(fundReleaseRequestExpiresDate)}`}.
                </span>
                {bankerComments &&
                  <div className={`text-danger ${isExpanded ? '' : 'd-block text-truncate'}`}>
                    <b>Funding Team Comments:</b> {bankerComments}
                  </div>
                }
              </div>
            </td>
            <td className='text-center'>
              {status === ChecklistItemStatus.Completed &&
                <span>{mdyyyy(checklistItemDate)}</span>}
            </td>
          </>
        }
        {isDisbursedLabel &&
          <>
            <td style={{ maxWidth: '120px' }}>
              <div className="fw-bold mb-1 pointer">{item.label}</div>
              <div className={`text-muted small pointer ${isExpanded ? '' : 'd-block text-truncate'}`}>{item.description}</div>
              {bankerComments &&
                <div className={`small text-danger ${isExpanded ? '' : 'd-block text-truncate'}`}>
                  <b>Funding Team Comments:</b> {bankerComments}
                </div>
              }
            </td>
            <td className='text-center'>
              <span>{mdyyyy(getDisbursedDateFromLabel(item.label, project))}</span>
            </td>
          </>
        }
        {isGenericAirtableLabel &&
          <>
            <td style={{ maxWidth: '300px' }}>
              <div className="fw-bold mb-1 pointer">{item.label}</div>
              <div className={`text-muted small pointer ${isExpanded ? '' : 'd-block text-truncate'}`}>
                {item.description}
              </div>
            </td>
            <td className='text-center'>
              {status === ChecklistItemStatus.Completed &&
                <span>{mdyyyy(checklistItemDate)}</span>}
            </td>
          </>
        }
        <td className="text-center">
          {!shouldHideStatusBadge && 
            <span className={`text-capitalize badge rounded-pill ${getBadgeClass(status)}`}>{status}</span>
          }
        </td>
        <td className="text-capitalize text-center">{item.owner}</td>
      </tr>
      {showModal && (
        <Modal
          onClose={handleCloseModal}
          title='Fund Release Request'
        >
          <p className="text-center mb-3">
            Clicking the 'Send Request' button will send the customer an email that will allow them to approve
            these funds for release. This is used instead of an invoice. To learn more,
            visit <a href={FUND_RELEASE_SUPPORT_LINK} target="_blank">oneethos.com/support/fund-release</a>
          </p>
        </Modal>
      )}
    </>
  )
}

export default ChecklistItemFundRequest